
// boarding-booking.js — FPS-510
// Custom element <boarding-booking> for the multi-step boarding booking form.
// Connects to the Randem middleware via window.FPSRandem.cloudUrl.

(function () {
  'use strict';

  // FPS-783: this store's customer service number, from the teifi_store.support_phone shop metafield
  // (snippets/randem-config.liquid). Falls back to Feeders' number until the metafield exists.
  const supportPhone = () => window.FPSRandem?.supportPhone || '(888) 667-1214';

  // ── Flatpickr loader (loaded once on first need) ──────────────────────────
  let _flatpickrReady = typeof flatpickr !== 'undefined';
  const _flatpickrCallbacks = [];

  function loadFlatpickr(cb) {
    if (_flatpickrReady) { cb(); return; }
    _flatpickrCallbacks.push(cb);
    if (_flatpickrCallbacks.length > 1) return;

    const link = document.createElement('link');
    link.rel = 'stylesheet';
    link.href = 'https://cdn.jsdelivr.net/npm/flatpickr@4/dist/flatpickr.min.css';
    document.head.appendChild(link);

    const script = document.createElement('script');
    script.src = 'https://cdn.jsdelivr.net/npm/flatpickr@4/dist/flatpickr.min.js';
    script.onload = () => {
      _flatpickrReady = true;
      _flatpickrCallbacks.splice(0).forEach(fn => fn());
    };
    script.onerror = () => console.error('[FPS Boarding] flatpickr failed to load from CDN');
    document.head.appendChild(script);
  }

  // See grooming-booking.js: a duplicate injection silently wins the race and pins the page to an
  // older build, so make it visible rather than letting it look like a fix that didn't land.
  if (customElements.get('boarding-booking')) {
    console.warn(
      '[FPS Boarding] boarding-booking.js loaded more than once. The first copy defined ' +
      '<boarding-booking> and this one is being ignored, so the running behaviour may be from an ' +
      'older build. Remove the duplicate <script> — the theme should load it only from ' +
      'sections/main-page-boarding-booking.liquid.'
    );
    return;
  }

  customElements.define(
    'boarding-booking',
    class BoardingBooking extends HTMLElement {

      // ── Lifecycle ──────────────────────────────────────────────────────────
      connectedCallback() {
        // connectedCallback fires again whenever the element is moved in the DOM, and every
        // listener bound below would be bound a second time — one click then runs each handler
        // twice. Harmless while pets were created on Randem's throwaway surface; once pets are
        // created against the customer's MyTime profile it produced two identical children per
        // click, and the second one made the first look like a duplicate name.
        if (this._initialized) return;
        this._initialized = true;

        this._BASE = `${(window.FPSRandem || {}).cloudUrl || ''}/api/PetBoarding`;
        this._config = window.FPSRandem || {};

        this._state = {
          userToken: null,
          customerId: null,
          userEmail: null,
          locationId: null,
          locationName: null,
          locationTz: 'America/New_York',

          // Step 1: Pets
          selectedPets: [],       // [{id, name, type}]

          // Step 2: Suite
          suiteVariationId: null,
          suiteName: null,
          suitePrice: null,

          // Step 3: Dates
          dropoffDate: null,
          pickupDate: null,
          dropoffDateTime: null,  // ISO with T07:00:00
          pickupDateTime: null,   // ISO with T12:00:00

          // Step 4: Daily add-ons
          addonVariationIds: [],
          addonNames: [],

          // Step 5: Grooming add-on
          groomingVariationId: null,
          groomingName: null,

          // Step 6: Group play
          groupPlayVariationId: null,
          groupPlayName: null,

          // Step 7: Vaccination
          vaccinationStatus: null,
          vaccinationFiles: [],

          // Misc
          appointmentNotes: '',
          cachedPets: [],
          cachedBreeds: [],
          cachedPetFormFields: [],
        };

        this._step1Ready = false;
        this._step2Ready = false;
        this._step3Ready = false;
        this._step3Ctrl = null;
        this._step4Ready = false;
        this._step5Ready = false;
        this._step6Ready = false;
        this._step7Ready = false;
        this._step8Ready = false;
        this._flatpickrDropoff = null;
        this._flatpickrPickup = null;
        this._flatpickrRange = null;
        this._flatpickrDob = null;
        this._step4Ctrl = null;
        this._step5Ctrl = null;
        this._step6Ctrl = null;
        this._step7Ctrl = null;

        this._bindElements();
        this._initAuth();
        this._initLocationList();
        this._initSkipButtons();

        // Book button listener is set once here — NOT inside _initStep8 which can
        // be called multiple times after step resets, causing duplicate requests.
        this._bookBtn?.addEventListener('click', () => this._handleCheckout());

        // No `location:changed` listener: the facility is chosen from the MyTime list in Step 1 and
        // is independent of the sitewide store picker (FPS-656). MyTime facilities are separate
        // records that don't map 1:1 to Shopify locations, so a sitewide store change must not
        // reset or re-bind this form.
      }

      // ── DOM references ─────────────────────────────────────────────────────
      _bindElements() {
        const q = sel => this.querySelector(sel);
        const qa = sel => this.querySelectorAll(sel);

        // Auth
        this._authSection = q('.fps-auth--boarding');
        this._signinForm = q('.fps-auth__form--signin');
        this._registerForm = q('.fps-auth__form--register');
        this._authEmail = q('#boarding-auth-email');
        this._authPassword = q('#boarding-auth-password');
        this._authSubmit = q('.fps-auth__submit');
        this._authRegLink = q('.fps-auth__register');
        this._authRegSubmit = q('.fps-auth__register-submit');
        this._authBackSignin = q('.fps-auth__back-signin');
        this._authError = q('.fps-auth--boarding .fps-auth__form--signin .fps-auth__error');
        this._authSignedIn = q('.fps-auth__signed-in');
        this._authSignedInEmail = q('.fps-auth__email');

        // Steps
        this._steps = {
          1: q('.step-1.fps-step--boarding'),
          2: q('.step-2.fps-step--boarding'),
          3: q('.step-3.fps-step--boarding'),
          4: q('.step-4.fps-step--boarding'),
          5: q('.step-5.fps-step--boarding'),
          6: q('.step-6.fps-step--boarding'),
          7: q('.step-7.fps-step--boarding'),
          8: q('.step-8.fps-step--boarding'),
        };
        this._summaryEl = q('.fps-boarding-summary');

        // Step 2: Pet selection
        this._petSelect = q('#boarding-pet-id');
        this._selectedPetsContainer = q('.fps-boarding-selected-pets');
        this._addPetLink = q('.fps-boarding-add-pet');
        this._addNewPetLink = q('.fps-boarding-add-new');
        this._newPetForm = q('.fps-boarding-new-pet');
        this._petNameInput = q('#boarding-pet-name');
        this._petWeightInput = q('#boarding-pet-weight');
        this._petTypeSelect = q('#boarding-pet-type');
        this._petGenderSelect = q('#boarding-pet-gender');
        this._petBreedSelect = q('#boarding-pet-breed');
        this._petDobInput = q('#boarding-pet-dob');
        this._savePetBtn = q('.fps-boarding-save-pet');
        this._cancelPetBtn = q('.fps-boarding-cancel-pet');

        // Step 1: Facility
        this._facilityInput = q('#boarding-facility');
        this._facilityResults = q('.fps-facility-results');

        // Step 2: Suites
        this._suitesGrid = q('.fps-suites');

        // Step 4: Dates (inline range calendar)
        this._dropoffInput = q('#boarding-dropoff');
        this._pickupInput = q('#boarding-pickup');
        this._dateRangeCalendar = q('#boarding-daterange-calendar');
        this._dateRangeDisplay = q('.fps-daterange-display');
        this._dateRangeDisplayText = q('.fps-daterange-display__text');

        // Step 5: Daily add-ons
        this._addonGrid = q('.fps-boarding-addon-grid');
        this._skipAddons = q('.step-5.fps-step--boarding .fps-step__skip');

        // Step 6: Grooming
        this._groomingGrid = q('.fps-grooming-addon-grid');
        this._skipGrooming = q('.step-6.fps-step--boarding .fps-step__skip');

        // Step 7: Group play
        this._groupPlayGrid = q('.fps-group-play-grid');
        this._skipGroupPlay = q('.step-7.fps-step--boarding .fps-step__skip');

        // Step 8: Vaccination
        this._vaccinationSelect = q('#boarding-vaccination-status');
        this._vaccinationUploadWrap = q('.fps-vaccination-upload-wrap');
        this._vaccinationUpload = q('#boarding-vaccination-upload');

        // Summary / book
        this._notesInput = q('#boarding-additional-notes');
        this._bookBtn = q('.fps-btn--book-request');
        this._summaryPetName = q('.fps-summary-row__value--pet-name');
        this._summaryServices = q('.fps-summary-row__value--services');
        this._summaryVaccination = q('.fps-summary-row__value--vaccination');
        this._summaryDropoff = q('.fps-summary-row__value--dropoff');
        this._summaryPickup = q('.fps-summary-row__value--pickup');
        this._summaryFacility = q('.fps-summary-row__value--facility');
        this._summarySuite = q('.fps-summary-row__value--suite');
        this._summaryAddons = q('.fps-summary-row__value--addons');
        this._summaryGrooming = q('.fps-summary-row__value--grooming');
        this._summaryGroupPlay = q('.fps-summary-row__value--groupplay');

        // Card templates
        this._tplSuiteCard = q('#fps-tpl-suite-card');
        this._tplAddonCardBoarding = q('#fps-tpl-addon-card-boarding');
        this._tplGroomingCard = q('#fps-tpl-grooming-card');
        this._tplGroupPlayCard = q('#fps-tpl-groupplay-card');

        // Loader elements — cached before grid.innerHTML clears them on reset
        this._step2Loader = this._suitesGrid?.querySelector('.fps-loader') || null;
        this._step4Loader = this._addonGrid?.querySelector('.fps-loader') || null;
        this._step5Loader = this._groomingGrid?.querySelector('.fps-loader') || null;
        this._step6Loader = this._groupPlayGrid?.querySelector('.fps-loader') || null;
      }

      // ── API helpers ────────────────────────────────────────────────────────
      async _get(path, auth = false, signal = undefined) {
        const headers = {};
        if (auth && this._state.userToken) headers['Authorization'] = this._state.userToken;
        const res = await fetch(`${this._BASE}${path}`, { headers, signal });
        if (!res.ok) {
          const err = new Error(`GET ${path} → ${res.status}`);
          err.status = res.status;
          throw err;
        }
        return res.json();
      }

      async _post(path, body = {}, auth = false) {
        const headers = { 'Content-Type': 'application/json' };
        if (auth && this._state.userToken) headers['Authorization'] = this._state.userToken;
        const res = await fetch(`${this._BASE}${path}`, {
          method: 'POST',
          headers,
          body: JSON.stringify(body),
        });
        if (!res.ok) {
          const err = new Error(`POST ${path} → ${res.status}`);
          err.status = res.status;
          throw err;
        }
        return res.json();
      }

      _assertRandemSuccess(data) {
        // Randem returns HTTP 200 with `isSuccessful: false` on auth failures —
        // surface the API's own message instead of treating the response as success.
        if (data && data.isSuccessful === false) {
          throw new Error(data.message || data.error || 'The booking service rejected the request.');
        }
      }

      async _postForm(path, formData) {
        const headers = {};
        if (this._state.userToken) headers['Authorization'] = this._state.userToken;
        const res = await fetch(`${this._BASE}${path}`, { method: 'POST', headers, body: formData });
        if (!res.ok) {
          const err = new Error(`POST ${path} → ${res.status}`);
          err.status = res.status;
          throw err;
        }
        return res.json();
      }

      // ── Auth ───────────────────────────────────────────────────────────────
      _initAuth() {
        const saved = sessionStorage.getItem('fps_boarding_token');
        if (saved) {
          try {
            const { token, email, customerId } = JSON.parse(saved);
            Object.assign(this._state, { userToken: token, userEmail: email, customerId });
            this._showSignedIn(email);
            return;
          } catch { }
        }

        this._authSubmit?.addEventListener('click', () => this._handleSignIn());
        this._authRegLink?.addEventListener('click', () => {
          this._signinForm.style.display = 'none';
          this._registerForm.style.display = '';
        });
        this._authBackSignin?.addEventListener('click', () => {
          this._registerForm.style.display = 'none';
          this._signinForm.style.display = '';
        });
        this._authRegSubmit?.addEventListener('click', () => this._handleRegister());
      }

      async _handleSignIn() {
        const email = this._authEmail?.value.trim();
        const password = this._authPassword?.value;
        if (!email || !password) { this._showAuthError('Please enter email and password.'); return; }

        this._authSubmit.disabled = true;
        this._authSubmit.textContent = 'Signing in…';
        this._clearAuthError();

        try {
          const data = await this._post('/SignIn', { Email: email, Password: password });
          this._assertRandemSuccess(data);
          const token = data.token || data.authentication_token || data.authToken;
          const customerId = data.id || data.customerId || data.customer_id;
          if (!token) throw new Error('No token returned by Randem.');
          Object.assign(this._state, { userToken: token, userEmail: email, customerId });
          sessionStorage.setItem('fps_boarding_token', JSON.stringify({ token, email, customerId }));
          this._showSignedIn(email);
        } catch (err) {
          this._showAuthError(
            err.name === `TypeError`
              ? `We could not reach the booking service. Please try again in a moment.`
              : `Incorrect password. Please try again.`
          );
        } finally {
          if (this._authSubmit) { this._authSubmit.disabled = false; this._authSubmit.textContent = 'SIGN IN'; }
        }
      }

      async _handleRegister() {
        const q = id => this.querySelector(id)?.value.trim();
        const firstName = q('#boarding-reg-first-name');
        const lastName = q('#boarding-reg-last-name');
        const email = q('#boarding-reg-email');
        const phone = q('#boarding-reg-phone');
        const zipCode = q('#boarding-reg-zipcode');
        const password = this.querySelector('#boarding-reg-password')?.value;

        if (!firstName || !lastName || !email || !password) {
          this._showAuthError('Please fill in all required fields.', true); return;
        }

        this._authRegSubmit.disabled = true;
        this._authRegSubmit.textContent = 'Creating account…';
        this._clearAuthError(true);
        try {
          const data = await this._post('/Register', {
            FirstName: firstName,
            LastName: lastName,
            Email: email,
            Phone: phone,
            ZipCode: zipCode,
            Password: password,
          });
          if (data.isSuccessful === false) {
            this._showAuthError('', true);
            return;
          }
          const token = data.token || data.authentication_token || data.authToken;
          const customerId = data.id || data.customerId || data.customer_id;
          if (!token) throw new Error('No token returned by Randem.');
          Object.assign(this._state, { userToken: token, userEmail: email, customerId });
          sessionStorage.setItem('fps_boarding_token', JSON.stringify({ token, email, customerId }));
          this._showSignedIn(email);
        } catch (err) {
          this._showAuthError(
            err.name === 'TypeError'
              ? 'We could not reach the booking service. Please try again in a moment.'
              : (err.message || 'Registration failed. Please try again.'),
            true
          );
        } finally {
          if (this._authRegSubmit) { this._authRegSubmit.disabled = false; this._authRegSubmit.textContent = 'Create Account'; }
        }
      }

      _showSignedIn(email) {
        if (this._signinForm) this._signinForm.style.display = 'none';
        if (this._registerForm) this._registerForm.style.display = 'none';
        if (this._authSignedIn) this._authSignedIn.style.display = '';
        if (this._authSignedInEmail) this._authSignedInEmail.textContent = email;
        // Auth (step 0) complete — open Step 1 so a facility can be picked from the MyTime list.
        this._enableFacilityStep();
      }

      _showAuthError(msg, isReg = false) {
        if (isReg) {
          // Register errors use a template-based <ul> for styled multi-reason messaging.
          const container = this._registerForm?.querySelector('.fps-reg-error-container');
          if (!container) return;
          container.innerHTML = '';
          const tpl = this.querySelector('#fps-tpl-reg-error');
          if (tpl) {
            container.appendChild(tpl.content.cloneNode(true));
          } else {
            // Fallback if template not found
            const p = document.createElement('p');
            p.className = 'fps-error text-[13px] mt-1';
            p.textContent = msg;
            container.appendChild(p);
          }
        } else {
          if (this._authError) { this._authError.textContent = msg; this._authError.style.display = ''; }
        }
      }

      _clearAuthError(isReg = false) {
        if (isReg) {
          const container = this._registerForm?.querySelector('.fps-reg-error-container');
          if (container) container.innerHTML = '';
        } else {
          if (this._authError) { this._authError.style.display = 'none'; this._authError.textContent = ''; }
        }
      }

      // ── Location ───────────────────────────────────────────────────────────
      // Step 1 opens as soon as the shopper is authenticated. Boarding facilities come from the
      // MyTime boarding /Locations API — the single source of truth for what is actually bookable.
      // There is no sitewide-store pre-selection (FPS-656): the client asked for a single location
      // selector, and MyTime facilities don't map 1:1 to Shopify locations, so the MyTime list plus
      // its search box are the only way to choose one. The single exception is an account with just
      // one facility, where there is nothing to choose and it is bound automatically.
      async _enableFacilityStep() {
        if (!this._state.userToken) return;

        let facilities;
        try {
          facilities = await this._get('/Locations');
        } catch (err) {
          console.error('[FPS Boarding] Location error:', err);
          this._showLocationPrompt(`Unable to load boarding facilities. Please call ${supportPhone()}.`);
          return;
        }
        facilities = Array.isArray(facilities) ? facilities : [];
        if (!facilities.length) {
          this._showLocationPrompt(`No boarding facilities are available right now. Please call ${supportPhone()}.`);
          return;
        }

        // Exactly one facility — bind it before Step 1 opens so _initStep1 carries the shopper
        // straight through to Step 2.
        if (facilities.length === 1) {
          this._selectFacility(facilities[0].id, facilities[0].nickname, facilities[0]);
        }

        this._clearLocationPrompt();
        this._enableStep(1);
      }

      // Bind a facility picked from the MyTime list (or the only one on the account). Deliberately
      // does NOT write the sitewide locationDrawerSelected cookie or dispatch `location:changed`
      // (FPS-656): the value here is a MyTime nickname ("Valley Station Boarding") that matches no
      // Shopify location, so writing it would corrupt the sitewide store context used by the
      // add-to-cart gate, delivery options and header store status.
      _selectFacility(locationId, locationName, facility) {
        this._state.locationId = locationId;
        this._state.locationName = locationName;
        this._state.locationTz = facility?.timeZoneName || 'America/New_York';
        this._updateSummary('facility', locationName);
      }

      _showLocationPrompt(msg) {
        const step1 = this._steps[1];
        if (!step1) return;
        step1.classList.remove('disabled');

        if (this._facilityResults) {
          this._facilityResults.style.display = '';
          this._facilityResults.innerHTML = `
            <div class="fps-location-prompt">
              <p class="text-[13px] text-[#5B6470]">${msg || 'Please <strong>choose a boarding facility</strong> below to continue.'}</p>
            </div>`;
        }
      }

      // Clear the "not available / select a store" message once a facility resolves, so a stale
      // warning doesn't linger after selection (mirrors grooming's _clearSalonMessage).
      _clearLocationPrompt() {
        if (this._facilityResults) {
          this._facilityResults.innerHTML = '';
          this._facilityResults.style.display = 'none';
        }
      }

      // ── Scroll helper — accounts for sticky header ─────────────────────────
      _scrollTo(el) {
        if (!el) return;
        const stickyHeader = document.querySelector('[is="sticky-header"]') ||
          document.querySelector('sticky-header') ||
          document.querySelector('.site-header--sticky');
        const headerH = stickyHeader ? stickyHeader.getBoundingClientRect().height : 0;
        const top = el.getBoundingClientRect().top + window.pageYOffset - headerH - 16;
        window.scrollTo({ top, behavior: 'smooth' });
      }

      // ── Step enablement ────────────────────────────────────────────────────
      _enableStep(num, { scroll = true } = {}) {

        if (num === 'summary') {
          this._summaryEl?.classList.remove('disabled');
          if (scroll) this._scrollTo(this._summaryEl);
          this._validateBooking();
          return;
        }
        const step = this._steps[num];
        if (!step) return;
        step.classList.remove('disabled');
        if (scroll) this._scrollTo(step);

        if (num === 1 && !this._step1Ready) { this._step1Ready = true; this._initStep1(); }
        if (num === 2 && !this._step2Ready) { this._step2Ready = true; this._loadStep2(); }
        if (num === 3 && !this._step3Ready) { this._step3Ready = true; this._loadStep3(); }
        if (num === 4 && !this._step4Ready) { this._step4Ready = true; this._loadStep4(); }
        if (num === 5 && !this._step5Ready) { this._step5Ready = true; this._loadStep5(); }
        if (num === 6 && !this._step6Ready) { this._step6Ready = true; this._loadStep6(); }
        if (num === 7 && !this._step7Ready) { this._step7Ready = true; this._loadStep7(); }
        if (num === 8 && !this._step8Ready) { this._step8Ready = true; this._initStep8(); }
      }

      _resetFromStep(from) {
        for (let i = from; i <= 8; i++) {
          this._steps[i]?.classList.add('disabled');
        }
        if (from <= 1) {
          this._step1Ready = false;
        }
        if (from <= 2) {
          this._step2Ready = false;
          this._state.selectedPets = [];
          if (this._selectedPetsContainer) { this._selectedPetsContainer.innerHTML = ''; this._selectedPetsContainer.style.display = 'none'; }
          if (this._newPetForm) this._newPetForm.style.display = 'none';
          if (this._addPetLink) this._addPetLink.style.display = 'none';
          const sw = this._petSelect?.closest('.fps-pet-select-wrap');
          if (sw) sw.style.display = '';
        }
        if (from <= 3) {
          this._step3Ctrl?.abort(); this._step3Ctrl = null;
          this._step3Ready = false;
          Object.assign(this._state, { suiteVariationId: null, suiteName: null, suitePrice: null });
          if (this._suitesGrid) { this._suitesGrid.innerHTML = ''; if (this._step2Loader) this._suitesGrid.appendChild(this._step2Loader); }
        }
        if (from <= 4) {
          this._step4Ctrl?.abort(); this._step4Ctrl = null;
          this._step4Ready = false;
          Object.assign(this._state, { dropoffDate: null, pickupDate: null, dropoffDateTime: null, pickupDateTime: null });
          this._flatpickrRange?.clear();
          this._flatpickrRange?.destroy(); this._flatpickrRange = null;
          if (this._dropoffInput) this._dropoffInput.value = '';
          if (this._pickupInput) this._pickupInput.value = '';
          if (this._dateRangeDisplay) this._dateRangeDisplay.style.display = 'none';
          if (this._dateRangeDisplayText) this._dateRangeDisplayText.textContent = '';
          if (this._dateRangeCalendar) this._dateRangeCalendar.innerHTML = '';
        }
        if (from <= 5) {
          this._step5Ctrl?.abort(); this._step5Ctrl = null;
          this._step5Ready = false;
          Object.assign(this._state, { addonVariationIds: [], addonNames: [] });
          if (this._addonGrid) { this._addonGrid.innerHTML = ''; if (this._step4Loader) this._addonGrid.appendChild(this._step4Loader); }
          const s5 = this._steps[5];
          if (s5) { s5.classList.remove('fps-step--skipped'); s5.querySelector('.fps-step__body')?.removeAttribute('style'); }
        }
        if (from <= 6) {
          this._step6Ctrl?.abort(); this._step6Ctrl = null;
          this._step6Ready = false;
          Object.assign(this._state, { groomingVariationId: null, groomingName: null });
          if (this._groomingGrid) { this._groomingGrid.innerHTML = ''; if (this._step5Loader) this._groomingGrid.appendChild(this._step5Loader); }
          const s6 = this._steps[6];
          if (s6) { s6.classList.remove('fps-step--skipped'); s6.querySelector('.fps-step__body')?.removeAttribute('style'); }
        }
        if (from <= 7) {
          this._step7Ctrl?.abort(); this._step7Ctrl = null;
          this._step7Ready = false;
          Object.assign(this._state, { groupPlayVariationId: null, groupPlayName: null });
          if (this._groupPlayGrid) { this._groupPlayGrid.innerHTML = ''; if (this._step6Loader) this._groupPlayGrid.appendChild(this._step6Loader); }
          const s7 = this._steps[7];
          if (s7) { s7.classList.remove('fps-step--skipped'); s7.querySelector('.fps-step__body')?.removeAttribute('style'); }
        }
        if (from <= 8) {
          this._step8Ready = false;
          Object.assign(this._state, { vaccinationStatus: null, vaccinationFiles: [] });
          if (this._vaccinationSelect) this._vaccinationSelect.value = '';
          if (this._vaccinationUploadWrap) this._vaccinationUploadWrap.style.display = 'none';
        }
        this._summaryEl?.classList.add('disabled');

        // Reset only summary fields that belong to steps being cleared,
        // then re-populate fields that are still valid from earlier steps.
        this._updateSummary('all-reset');
        if (from > 1 && this._state.locationName) {
          this._updateSummary('facility', this._state.locationName);
        }
        if (from > 2 && this._state.selectedPets.length) {
          this._updateSummary('pet-name', this._state.selectedPets.map(p => p.name).join(', '));
        }
        if (from > 3 && this._state.suiteName) {
          this._updateSummary('suite', `${this._state.suiteName} — $${this._state.suitePrice}/night`);
        }
        if (from > 4 && this._state.dropoffDate) {
          this._updateSummary('dropoff', this._formatDate(this._state.dropoffDate));
          this._updateSummary('pickup', this._formatDate(this._state.pickupDate));
        }

        this._validateBooking();
      }

      // ── Loader helpers ─────────────────────────────────────────────────────
      _showLoader(container) { const l = container?.querySelector('.fps-loader'); if (l) l.style.display = ''; }
      _hideLoader(container) { const l = container?.querySelector('.fps-loader'); if (l) l.style.display = 'none'; }

      // ── Custom select ──────────────────────────────────────────────────────

      // ── Skip buttons — toggle skip/unskip state ───────────────────────────
      _initSkipButtons() {
        const toggleSkip = (btn, stepEl, summaryKey, nextNum) => {
          const body = stepEl?.querySelector('.fps-step__body');
          const isSkipped = stepEl?.classList.contains('fps-step--skipped');

          if (isSkipped) {
            // Unskip: restore content
            stepEl.classList.remove('fps-step--skipped');
            if (body) body.style.display = '';
            btn.textContent = 'Skip';
            this._updateSummary(summaryKey, '—');
          } else {
            // Skip: hide content
            if (body) body.style.display = 'none';
            stepEl?.classList.add('fps-step--skipped');
            btn.textContent = 'Unskip';
            this._updateSummary(summaryKey, 'None');
            if (nextNum) this._scrollTo(this._steps[nextNum]);
          }
        };

        this._skipAddons?.addEventListener('click', () =>
          toggleSkip(this._skipAddons, this._steps[5], 'addons', 6));
        this._skipGrooming?.addEventListener('click', () =>
          toggleSkip(this._skipGrooming, this._steps[6], 'grooming', 7));
        this._skipGroupPlay?.addEventListener('click', () =>
          toggleSkip(this._skipGroupPlay, this._steps[7], 'groupplay', 8));
      }

      // ── Step 1 — Facility selection ───────────────────────────────────────
      // Render Step 1 from the MyTime boarding /Locations API (the source of truth for bookable
      // facilities) as a grouped, searchable list mirroring grooming. A single-facility account is
      // still listed, and is pre-bound by _enableFacilityStep so Step 2 opens immediately.
      async _renderBoardingLocations() {
        const listEl = this.querySelector('.fps-facility-list');
        if (!listEl) return;
        const box = listEl.closest('.fps-facility-box');
        const status = msg => { listEl.innerHTML = `<p class="fps-facility-list__status px-3.5 py-3 text-[12px]">${msg}</p>`; };

        let locs;
        try {
          locs = await this._get('/Locations');
        } catch (_e) {
          if (box) box.style.display = '';
          status(`Unable to load boarding facilities. Please call ${supportPhone()}.`);
          return;
        }
        locs = Array.isArray(locs) ? locs : [];

        // No facilities -> hide the box (_enableFacilityStep shows a prompt). One or more -> always
        // render the list so the shopper can see and pick an available boarding facility.
        if (!locs.length) { if (box) box.style.display = 'none'; return; }

        if (box) box.style.display = '';
        // Cache the facilities (they carry lat/lng) so the search box can rank by distance, then
        // paint the default grouped-by-state list.
        this._facilities = locs;
        this._renderFacilityGroups(locs);
      }

      _escText(s) {
        return String(s ?? '').replace(/[&<>"]/g, c => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' }[c]));
      }

      // Card markup shared by the grouped list and the distance-ranked results.
      _facilityCardHtml(loc, mi) {
        const street = [loc.streetAddress, loc.streetAddress_2].filter(Boolean).join(' ');
        const addressLine = [street, loc.city, loc.state, loc.zipCode].filter(Boolean).join(', ');
        const distLabel = (mi != null && isFinite(mi))
          ? ` <span class="text-[11px] font-normal text-[#5B6470]">· ${Math.round(mi)} mi</span>`
          : '';
        return `
            <div class="group location-card px-3.5 py-3 flex items-center justify-between border-b border-b-[#D9DDE2]">
              <input type="radio" name="boardingLocationSelected" class="hidden" value="${this._escText(loc.nickname)}" data-location-id="${this._escText(loc.id)}" data-zipcode="${this._escText(loc.zipCode)}" hidden>
              <div class="grid grid-cols-1 gap-1">
                <p class="font-bold text-[14px]">${this._escText(loc.nickname)}${distLabel}</p>
                <small class="text-[12px]">${this._escText(addressLine)}</small>
              </div>
              <button class="btn-location btn py-2.5 px-4.5 text-[12px] bg-[#E6F2F8] text-[#0E6DA0]">Select</button>
            </div>`;
      }

      // Default view: facilities grouped by state.
      _renderFacilityGroups(facilities) {
        const listEl = this.querySelector('.fps-facility-list');
        if (!listEl) return;
        const sorted = [...facilities].sort((a, b) => (a.state || '').localeCompare(b.state || '') || (a.nickname || '').localeCompare(b.nickname || ''));
        let html = '';
        let currentState = null;
        for (const loc of sorted) {
          const state = (loc.state || '').toUpperCase();
          if (state !== currentState) {
            currentState = state;
            html += `<div class="header-list-collection border-b border-b-[#D9DDE2] bg-[#F7F8FA] py-2 px-3.5 highlight text-[11px] font-bold">${this._escText(state)}</div>`;
          }
          html += this._facilityCardHtml(loc);
        }
        listEl.innerHTML = html;
        this._proximityActive = false;
        this._syncFacilityRadios(this._state.locationName, this._state.locationId);
      }

      // Match on id alone when one is available — name matching is only a fallback for radios
      // rendered before an id existed. MyTime nicknames are not unique across states (the grooming
      // feed has two "Clarksville - Grooming Salon"), so an id-OR-name match can select a facility
      // the shopper didn't pick.
      _syncFacilityRadios(name, id) {
        const step1 = this._steps[1];
        if (!step1) return;
        const hasId = id != null && id !== '';
        step1.querySelectorAll('input[name="boardingLocationSelected"]').forEach(r => {
          const match = hasId
            ? String(r.dataset.locationId) === String(id)
            : (!!name && r.value === name);
          r.checked = match;
          r.closest('.group')?.classList.toggle('is-selected', match);
        });
      }

      _pruneEmptyStateHeaders(step1) {
        if (!step1) return;
        step1.querySelectorAll('.header-list-collection').forEach(header => {
          let sib = header.nextElementSibling, anyVisible = false;
          while (sib && !sib.classList.contains('header-list-collection')) {
            if (sib.classList.contains('location-card') && sib.style.display !== 'none') { anyVisible = true; break; }
            sib = sib.nextElementSibling;
          }
          header.style.display = anyVisible ? '' : 'none';
        });
      }

      // Great-circle distance in miles.
      _milesBetween(lat1, lng1, lat2, lng2) {
        const R = 3958.8, toRad = d => (d * Math.PI) / 180;
        const dLat = toRad(lat2 - lat1), dLng = toRad(lng2 - lng1);
        const a = Math.sin(dLat / 2) ** 2 + Math.cos(toRad(lat1)) * Math.cos(toRad(lat2)) * Math.sin(dLng / 2) ** 2;
        return 2 * R * Math.asin(Math.min(1, Math.sqrt(a)));
      }

      // A US zip, or '' if the input isn't one. Accepts zip+4 and stray whitespace so a pasted
      // "40216-1234" still searches on 40216.
      _normalizeZip(raw) {
        const m = (raw || '').trim().match(/^(\d{5})(?:[\s-]*\d{4})?$/);
        return m ? m[1] : '';
      }

      // Coordinates to measure the 50-mile radius from, or null if the zip can't be placed.
      // Tries the facility feed before Google: every MyTime location carries its own zipCode plus
      // lat/lng, so a zip that belongs to a facility can be placed with no network call and no Maps
      // key — the ranked "within 50 miles" view then survives a missing, referrer-restricted or
      // over-quota key instead of dropping to a flat unranked list.
      async _resolveZipOrigin(zip) {
        const facilities = this._facilities || [];
        const local = facilities.find(s =>
          String(s.zipCode || '').trim().slice(0, 5) === zip && s.lat != null && s.lng != null
        );
        if (local) return { lat: +local.lat, lng: +local.lng };

        const apiKey = document.querySelector('[data-google-maps-api-key]')?.dataset.googleMapsApiKey || '';
        if (!apiKey || !window.GoogleMapsGeocoder) return null;
        try {
          const geo = await window.GoogleMapsGeocoder.geocodeAddress(zip, apiKey);
          if (geo && geo.latitude != null && geo.longitude != null) {
            return { lat: +geo.latitude, lng: +geo.longitude };
          }
        } catch (_e) { /* geocode failed — fall through */ }
        return null;
      }

      // Zip search: place the zip, then show boarding facilities within 50 miles, nearest first.
      async _proximitySearch(zip) {
        const listEl = this.querySelector('.fps-facility-list');
        if (!listEl) return;
        const box = listEl.closest('.fps-facility-box');
        if (box) box.style.display = '';
        const status = msg => `<p class="fps-facility-list__status px-3.5 py-3 text-[12px]">${msg}</p>`;

        const origin = await this._resolveZipOrigin(zip);

        if (this._facilityInput && this._normalizeZip(this._facilityInput.value) !== zip) return;
        // Couldn't place the zip at all. Keep every facility on screen and say why the list isn't
        // ranked, instead of leaving the shopper with an empty step.
        if (!origin) {
          this._renderFacilityGroups(this._facilities || []);
          listEl.insertAdjacentHTML('afterbegin', status(`Couldn’t locate “${this._escText(zip)}”. Showing all boarding facilities — search by city or facility name to narrow the list.`));
          return;
        }

        const ranked = (this._facilities || [])
          .filter(s => s.lat != null && s.lng != null)
          .map(s => ({ s, mi: this._milesBetween(origin.lat, origin.lng, +s.lat, +s.lng) }))
          .sort((a, b) => a.mi - b.mi);
        const within = ranked.filter(x => x.mi <= 50);
        this._proximityActive = true;

        let html;
        if (within.length) {
          html = status(`${within.length} boarding facilit${within.length > 1 ? 'ies' : 'y'} within 50 miles of ${this._escText(zip)}, nearest first:`);
          for (const x of within) html += this._facilityCardHtml(x.s, x.mi);
        } else {
          const nearest = ranked[0];
          html = status(`No boarding facilities within 50 miles of ${this._escText(zip)}.` + (nearest ? ` Nearest is ${this._escText(nearest.s.nickname)}:` : ''));
          if (nearest) html += this._facilityCardHtml(nearest.s, nearest.mi);
        }
        listEl.innerHTML = html;
        this._syncFacilityRadios(this._state.locationName, this._state.locationId);
      }

      _initLocationList() {
        const step1 = this._steps[1];
        if (!step1) return;

        // Populate Step 1 from the MyTime boarding API (see _renderBoardingLocations).
        this._renderBoardingLocations();

        // Search box: a full 5-digit zip → facilities within 50 miles, nearest first; anything else
        // → name/zip text filter over the grouped list.
        let _facilityDebounce;
        this._facilityInput?.addEventListener('input', () => {
          clearTimeout(_facilityDebounce);
          _facilityDebounce = setTimeout(() => {
            const raw = (this._facilityInput.value || '').trim();
            const zipQuery = this._normalizeZip(raw);
            if (zipQuery) { this._proximitySearch(zipQuery); return; }
            if (this._proximityActive) this._renderFacilityGroups(this._facilities || []);

            // Narrow as the shopper types: a partial zip matches the facility's own zip prefix,
            // anything else matches the name.
            const partialZip = /^\d{1,4}$/.test(raw) ? raw : '';
            const q = raw.toLowerCase();
            step1.querySelectorAll('.group.location-card').forEach(card => {
              const name = (card.querySelector('p.font-bold')?.textContent || '').toLowerCase();
              const zip = (card.querySelector('input[name="boardingLocationSelected"]')?.dataset.zipcode || '');
              card.style.display = partialZip
                ? (String(zip).startsWith(partialZip) ? '' : 'none')
                : ((!q || name.includes(q) || zip.toLowerCase().includes(q)) ? '' : 'none');
            });
            this._pruneEmptyStateHeaders(step1);
          }, 150);
        });

        step1.addEventListener('click', e => {
          const btn = e.target.closest('button');
          if (!btn) return;
          const group = btn.closest('.group');
          if (!group) return;
          const radio = group.querySelector('input[name="boardingLocationSelected"]');
          if (!radio) return;

          step1.querySelectorAll('input[name="boardingLocationSelected"]').forEach(r => {
            r.checked = false;
            r.closest('.group')?.classList.remove('is-selected');
          });
          radio.checked = true;
          group.classList.add('is-selected');

          const locationName = radio.value;
          if (!locationName) return;
          const locationId = radio.dataset.locationId || '';

          // The card was rendered from the MyTime boarding /Locations feed, so this facility is
          // bookable by construction — bind straight from the cached record.
          const facility = (this._facilities || []).find(f => String(f.id) === String(locationId));

          this._resetFromStep(2);
          this._selectFacility(locationId, locationName, facility);

          if (this._facilityInput) this._facilityInput.value = locationName;
          this._clearLocationPrompt();
          this._enableStep(2);
        });
      }

      _initStep1() {
        const name = this._state.locationName;
        if (this._facilityInput) {
          this._facilityInput.value = name || '';
        }
        // Sync radio selection
        const step1 = this._steps[1];
        step1?.querySelectorAll('input[name="boardingLocationSelected"]').forEach(r => {
          const match = !!name && r.value === name;
          r.checked = match;
          r.closest('.group')?.classList.toggle('is-selected', match);
        });
        // Proceed to pet selection only when location is resolved

        if (this._state.locationId) {
          this._enableStep(2);
        }
      }

      // ── Step 2 — Pet selection ─────────────────────────────────────────────
      async _loadStep2() {
        // "Add pet to room" only appears after first pet is added
        if (this._addPetLink) this._addPetLink.style.display = 'none';
        // Ensure pet select is visible when step 2 loads/reloads
        const selectWrap = this._petSelect?.closest('.fps-pet-select-wrap');
        if (selectWrap) selectWrap.style.display = '';

        const step1 = this._steps[2];
        const loader = step1?.querySelector('.fps-loader');
        if (loader) loader.style.display = '';

        try {
          const rawPets = await this._get(`/CustomerPets?token=${encodeURIComponent(this._state.userToken || '')}`);

          this._state.cachedPets = (Array.isArray(rawPets) ? rawPets : []).map(p => this._normalizePet(p));

          if (loader) loader.style.display = 'none';

          // Populate #boarding-pet-id <select> with saved pets
          if (this._petSelect) {
            // Remove any previously-injected dynamic options, keep the blank default
            [...this._petSelect.options].forEach(o => { if (o.value) o.remove(); });
            if (!this._state.cachedPets.length) {
              this._petSelect.closest('.fps-pet-select-wrap')?.setAttribute('style', 'display:none');
              // No existing pets — open the new-pet form directly so the user can proceed
              this._showNewPetForm();
            } else {
              this._state.cachedPets.forEach(pet => {
                const opt = document.createElement('option');
                opt.value = String(pet.id);
                opt.textContent = `${pet.name} (${pet.type || 'Pet'})`;
                this._petSelect.appendChild(opt);
              });
            }
          }
        } catch (err) {
          console.error('[FPS Boarding Step 2] CustomerPets fetch error:', err);
          if (loader) loader.style.display = 'none';
        }

        // Pet select change — saved pets; new pet handled by .fps-boarding-add-pet link
        this._petSelect?.addEventListener('change', e => {
          const val = e.target.value;

          if (!val) return;
          const pet = this._state.cachedPets.find(p => String(p.id) === val);
          if (pet) {

            this._addPetChip({ id: pet.id, name: pet.name, type: pet.type, breed: pet.breed });
          }
          e.target.value = '';
        });

        // "Add Pet to Room" — second pet only, so filter the existing-pets dropdown to Dogs
        this._addPetLink?.addEventListener('click', () => {
          this._filterPetSelectByType(this._state.selectedPets[0]?.type);
          const sw = this._petSelect?.closest('.fps-pet-select-wrap');
          if (sw) sw.style.display = '';
          this._showNewPetForm();
        });

        // Pet form buttons
        this._addNewPetLink?.addEventListener('click', () => this._showNewPetForm());
        this._cancelPetBtn?.addEventListener('click', () => this._hideNewPetForm());
        this._savePetBtn?.addEventListener('click', () => this._handleSavePet());
      }

      // Saved pets come back as { petId, details: [{ title, value }, ...] } with titles
      // matching the PetForm field definitions. Flatten to the shape the dropdown +
      // chips expect. NOTE: the exact `value` format for Selection/Taggable fields is
      // inferred from the live BC widget and should be confirmed against a populated pet.
      _normalizePet(raw) {
        if (!raw || !Array.isArray(raw.details)) return raw; // already-flat fallback
        const field = title => raw.details.find(f => f.title === title) || {};
        const val = title => field(title).value ?? '';
        const breed = field('Pet Breed');

        // Selection fields store the option id, so a pet saved by this form came back as
        // "Scamp (2147490880)" in the dropdown. Resolve the id to its display name using the
        // options on the detail itself, falling back to the cached PetForm definitions.
        const resolveOption = (title) => {
          const f = field(title);
          const v = f.value ?? '';
          if (!v) return '';
          const opts = f.customFieldOptions
            || (this._state.cachedPetFormFields || []).find(d => d.title === title)?.options
            || [];
          const hit = opts.find(o => String(o.id ?? o.optionId) === String(v));
          const name = hit?.name ?? hit?.option_value;
          return name ? String(name).replace(/[\r\n]/g, '').trim() : v;
        };

        return {
          id: raw.petId ?? raw.id,
          name: val('Pet Name'),
          type: resolveOption('Pet Type'),
          breed: val('Pet Breed'),
          breed_id: breed.optionId ?? breed.id ?? '',
          gender: val('Gender'),
          birthdate: val('Date of Birth'),
          weight: val('Weight'),
          _raw: raw,
        };
      }

      _showNewPetForm() {
        if (!this._newPetForm) return;
        this._newPetForm.style.display = '';
        if (this._addNewPetLink) this._addNewPetLink.style.display = 'none';

        // When adding a second pet, lock the type select to the same type as the first pet.
        const isSecondPet = this._state.selectedPets.length >= 1;
        if (isSecondPet && this._petTypeSelect) {
          const firstPetType = (this._state.selectedPets[0]?.type || '').toLowerCase();
          const matchingOpt = [...this._petTypeSelect.options].find(
            opt => opt.value && opt.value.toLowerCase() === firstPetType
          );
          if (matchingOpt) this._petTypeSelect.value = matchingOpt.value;
          [...this._petTypeSelect.options].forEach(opt => {
            opt.hidden = opt.value !== '' && opt.value.toLowerCase() !== firstPetType;
            opt.disabled = opt.hidden;
          });
          if (matchingOpt) this._loadBreeds(matchingOpt.value);
        } else if (this._petTypeSelect) {
          // First pet — restore all options
          [...this._petTypeSelect.options].forEach(opt => { opt.hidden = false; opt.disabled = false; });
        }

        if (!this._petTypeSelect?._typeListenerAdded) {
          this._petTypeSelect?.addEventListener('change', e => {
            this._state.petType = e.target.value;
            if (e.target.value) this._loadBreeds(e.target.value);
          });
          if (this._petTypeSelect) this._petTypeSelect._typeListenerAdded = true;
        }

        if (this._petDobInput && !this._flatpickrDob) {
          loadFlatpickr(() => {
            this._flatpickrDob = flatpickr(this._petDobInput, {
              dateFormat: 'Y-m-d', maxDate: 'today', disableMobile: true,
              onChange: ([d]) => {
                if (!d) return;
                const y = d.getFullYear();
                const m = String(d.getMonth() + 1).padStart(2, '0');
                const day = String(d.getDate()).padStart(2, '0');
                this._petDobInput.value = `${y}-${m}-${day}`;
              },
            });
          });
        }

      }

      _hideNewPetForm() {
        if (this._newPetForm) this._newPetForm.style.display = 'none';
        if (this._addNewPetLink) this._addNewPetLink.style.display = '';
        if (this._petNameInput) this._petNameInput.value = '';
        if (this._petWeightInput) this._petWeightInput.value = '';
        if (this._petBreedSelect) { this._petBreedSelect.value = ''; this._petBreedSelect.disabled = true; this._petBreedSelect.innerHTML = '<option value="">Select pet type first</option>'; }
        if (this._petDobInput) this._petDobInput.value = '';
        if (this._petTypeSelect) this._petTypeSelect.value = '';
        if (this._petGenderSelect) this._petGenderSelect.value = '';
      }

      async _loadBreeds(petType) {

        try {
          const petFormId = this._config.boardingPetFormId;
          const data = await this._get(`/PetForm?petFormId=${petFormId}&petType=${petType}`);
          // PetForm returns an array of form field definitions
          const formFields = Array.isArray(data) ? data : (data.fields || data.petForm || []);


          if (formFields.length) this._state.cachedPetFormFields = formFields;

          // Extract breeds from the "Pet Breed" TaggableField options
          const breedField = formFields.find(f => f.title === 'Pet Breed');
          this._state.cachedBreeds = (breedField?.options || []).map(b => ({
            id: b.id,
            name: b.name.trim(),
          }));


          // Populate breed select
          if (this._petBreedSelect) {
            this._petBreedSelect.innerHTML = '<option value="">Select breed</option>';
            this._state.cachedBreeds.forEach(b => {
              const opt = document.createElement('option');
              opt.value = b.id;
              opt.textContent = b.name;
              this._petBreedSelect.appendChild(opt);
            });
            this._petBreedSelect.disabled = false;
          }
        } catch (err) {
          console.error('[FPS Boarding] Breeds error:', err);
        }
      }

      _buildPetDetails(name, type, breed, gender, weight, dob) {
        const fields = this._state.cachedPetFormFields || [];
        const formId = this._config.boardingPetFormId;

        const findField = (title) => fields.find(f => f.title === title);

        // Selection fields send the option's display name ("Dog"), matching what MyTime stores
        // natively — the grooming form used to send the option id, which is why some pets read
        // "Scamp (2147490880)" in the picker.
        const selectionValue = (field, displayValue) => {
          const v = String(displayValue ?? '');
          if (!field?.options?.length) return v;
          const match = field.options.find(o => (o.name ?? '').toLowerCase() === v.toLowerCase());
          return match ? String(match.name).replace(/[\r\n]/g, '').trim() : v;
        };

        const typeField = findField('Pet Type');

        const entries = [
          { field: findField('Pet Name'), formValue: String(name ?? '').trim() },
          { field: typeField, formValue: selectionValue(typeField, type) },
          { field: findField('Pet Breed'), formValue: String(breed ?? '').trim() },
          { field: findField('Weight'), formValue: String(weight || '') },
          { field: findField('Date of Birth'), formValue: String(dob ?? '').trim() },
        ];

        return entries
          .filter(e => e.field?.formFieldId != null && e.formValue !== '')
          .map(e => ({
            formId,
            formFieldId: String(e.field.formFieldId),
            formValue: e.formValue,
          }));
      }


      // Apply the pet's details to the child record _createPetOnProfile allocated, by writing
      // MyTime's intake form. Mirrors the grooming form — see the long note there.
      // Boarding uses its own form (45154) with the same field ids.
      //
      // Goes through the booking proxy (POST /PetIntakeForm), which owns the MyTime company and the
      // boarding intake form id and runs the write on the shopper's own token.
      async _applyPetIntakeForm(petId) {
        if (!petId || !this._state.userToken) return { ok: false };

        const items = {};
        if ((this._state.cachedPetFormFields || []).length) {
          this._buildPetDetails(
            this._petNameInput?.value.trim(),
            this._petTypeSelect?.value || '',
            (this._petBreedSelect?.options?.[this._petBreedSelect?.selectedIndex]?.textContent) || '',
            this._petGenderSelect?.value || '',
            parseFloat(this._petWeightInput?.value) || 0,
            this._petDobInput?.value || ''
          ).forEach(d => { items[d.formFieldId] = d.formValue; });
        }
        if (!Object.keys(items).length) return { ok: false };

        try {
          const res = await fetch(`${this._BASE}/PetIntakeForm`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              Authorization: this._state.userToken,
            },
            body: JSON.stringify({ petId, items }),
          });
          if (!res.ok) {
            // MyTime keys its complaints by its own custom field id (338077 = Pet Name).
            const body = await res.json().catch(() => null);
            const reasons = this._readMyTimeErrors(body?.errors);
            console.error('[FPS Boarding] pet intake form rejected:', res.status, reasons);
            return { ok: false, reasons, duplicateName: this._isDuplicateNameError(reasons) };
          }
          return { ok: true };
        } catch (err) {
          console.error('[FPS Boarding] pet intake form failed:', err?.message || err);
          return { ok: false };
        }
      }

      // MyTime keys its complaints two ways: by its own custom field id on the intake form
      // (338077 = Pet Name) and under `general` on /user/children. Both are objects of arrays.
      _readMyTimeErrors(body) {
        return Object.values(body?.errors || {})
          .flat()
          .map(e => e?.type)
          .filter(Boolean);
      }

      // The same rejection is spelled differently per endpoint: the intake form answers
      // `cannot_have_same_pet_name_as_other_pets`, /user/children prefixes it with `validation_`.
      // Matching on the suffix keeps the shopper-facing "pick another name" prompt firing on both.
      _isDuplicateNameError(reasons) {
        return (reasons || []).some(r => String(r).endsWith('cannot_have_same_pet_name_as_other_pets'));
      }

      // Create the pet against the customer's MyTime profile and return its child id.
      //
      // Replaces Randem's POST /CreatePet, whose record belongs to the appointment rather than the
      // customer: proved on a clean account (2026-08-13, FPS-680) where CreatePet answered 200 and
      // both GET /user/children and Randem's own /CustomerPets stayed empty afterwards, so no
      // saved pet ever reached the picker. The pet's name goes in `first_name` — `child_name`, the
      // label MyTime uses in its errors, is not an accepted parameter.
      async _createPetOnProfile(petName) {
        if (!this._state.userToken) {
          throw new Error('Not signed in — cannot save the pet');
        }

        const res = await fetch(`${this._BASE}/Pets`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            accept: 'application/json',
            Authorization: this._state.userToken,
          },
          body: JSON.stringify({ petName }),
        });

        const data = await res.json().catch(() => null);
        const petId = data?.petId;
        if (!res.ok || !petId) {
          const err = new Error(`Pet creation failed (HTTP ${res.status})`);
          err.status = res.status;
          err.reasons = this._readMyTimeErrors(data?.errors);
          err.duplicateName = this._isDuplicateNameError(err.reasons);
          throw err;
        }
        return petId;
      }

      async _handleSavePet() {
        const name = this._petNameInput?.value.trim();
        const weight = parseFloat(this._petWeightInput?.value) || 0;
        const type = this._petTypeSelect?.value || '';
        const breedOpt = this._petBreedSelect?.options?.[this._petBreedSelect?.selectedIndex];
        const breed = breedOpt?.textContent || '';
        const gender = this._petGenderSelect?.value || '';
        const dob = this._petDobInput?.value || '';



        if (!name || !type) { alert('Please enter pet name and type.'); return; }

        // Second line of defence against a doubled handler: two runs in the same tick both get
        // past the button's disabled flag, and each would create its own child.
        if (this._savingPet) return;
        this._savingPet = true;

        this._savePetBtn.disabled = true;
        this._savePetBtn.textContent = 'Saving…';

        try {
          // cachedPetFormFields is only populated by _loadBreeds(), i.e. after a pet type is
          // chosen. If that fetch hasn't run or failed, _buildPetDetails can't resolve a single
          // field and returns [], leaving a child with a name and no details. Load the form on
          // demand, then refuse rather than create an incomplete pet.
          if (!(this._state.cachedPetFormFields || []).length) await this._loadBreeds(type);

          const petDetails = this._buildPetDetails(name, type, breed, gender, weight, dob);

          // Explicitly require the Pet Name entry, not just a non-empty payload: a pet created
          // without one is accepted with a 200 and named after MyTime's internal GUID, which is
          // how a grooming booking reached the salon as "5932cbac-9394-43ac-bc75-5129b51be540".
          const nameFieldId = String(
            (this._state.cachedPetFormFields || []).find(f => f.title === 'Pet Name')?.formFieldId ?? ''
          );
          if (!petDetails.length || !nameFieldId || !petDetails.some(d => d.formFieldId === nameFieldId)) {
            // Log which fields resolved, never their values — the payload holds customer-entered
            // pet details (name, birth date, breed, weight, gender).
            console.error(
              '[FPS Boarding] pet creation blocked — no Pet Name in payload. Field ids present:',
              petDetails.map(d => d.formFieldId)
            );
            throw new Error('Pet intake form unavailable — refusing to create an incomplete pet');
          }

          // A previous attempt may have allocated a pet that MyTime then refused to name (duplicate).
          // Reuse it rather than creating another, which would leave the first one unnamed.
          let petId = this._pendingPetId;
          if (!petId) {
            try {
              petId = await this._createPetOnProfile(name);
            } catch (err) {
              console.error('[FPS Boarding Step 2] pet creation failed:', err?.status ?? '', err?.reasons || err?.message || err);
              alert(
                err?.duplicateName
                  ? 'You already have a pet with this name. Please give this pet a different name.'
                  : 'Failed to save pet. Please try again.'
              );
              return;
            }
            this._pendingPetId = petId;
          }

          // Creating the child stores only its name. The rest of the pet's details are MyTime custom
          // fields, applied by the intake form — without it the record carries a name and nothing
          // else. Randem's own flow has this second step but the middleware route for it
          // (UpdateCustomerPet) 404s in production; see _applyPetIntakeForm.
          const intake = await this._applyPetIntakeForm(petId);
          if (intake.duplicateName) {
            // Keep _pendingPetId so the retry renames this record instead of stranding it.
            alert('You already have a pet with this name. Please give this pet a different name.');
            return;
          }
          this._pendingPetId = null;

          const pet = { id: petId, name, type, breed, birthdate: dob, weight, gender };
          this._state.cachedPets.push(pet);
          this._hideNewPetForm();
          this._addPetChip(pet);
        } catch (err) {
          console.error('[FPS Boarding Step 2] save pet error:', err);
          alert('Failed to save pet. Please try again.');
        } finally {
          this._savingPet = false;
          if (this._savePetBtn) { this._savePetBtn.disabled = false; this._savePetBtn.textContent = 'ADD PET'; }
        }
      }

      _addPetChip(pet) {
        // Without an id the pet cannot be booked — /Checkout would post petId: "undefined".
        if (!pet?.id) { console.error('[FPS Boarding] pet has no id, not adding:', pet); return; }
        if (this._state.selectedPets.find(p => String(p.id) === String(pet.id))) return;
        if (this._state.selectedPets.length >= 2) return; // max 2 pets per booking

        this._state.selectedPets.push(pet);

        if (this._selectedPetsContainer) {
          this._selectedPetsContainer.style.display = '';
          const chip = document.createElement('div');
          chip.className = 'fps-pet-chip';
          chip.dataset.petId = pet.id;
          chip.innerHTML = `<span>${pet.name}</span><button type="button" class="fps-pet-chip__remove" aria-label="Remove">×</button>`;
          chip.querySelector('.fps-pet-chip__remove').addEventListener('click', () => this._removePetChip(pet.id));
          this._selectedPetsContainer.appendChild(chip);
        }

        this._updateSummary('pet-name', this._state.selectedPets.map(p => p.name).join(', '));

        if (this._state.selectedPets.length === 1) {
          // "Add Pet to Room" only makes sense for dog bookings (Double Suite is dogs-only)
          const isDog = (pet.type || '').toLowerCase() === 'dog';
          if (this._addPetLink) this._addPetLink.style.display = isDog ? '' : 'none';
          this._filterPetSelectByType(pet.type);
          this._enableStep(3);
        } else {
          // Second pet — hide add button, reload suites so Double Suite appears
          if (this._addPetLink) this._addPetLink.style.display = 'none';
          const sw = this._petSelect?.closest('.fps-pet-select-wrap');
          if (sw) sw.style.display = 'none';
          this._resetFromStep(3);
          this._enableStep(3, { scroll: false });
        }
      }

      _filterPetSelectByType(type) {
        if (!this._petSelect) return;
        const t = (type || '').toLowerCase();
        [...this._petSelect.options].forEach(opt => {
          if (!opt.value) return;
          const pet = this._state.cachedPets.find(p => String(p.id) === opt.value);
          const match = (pet?.type || '').toLowerCase() === t;
          opt.hidden = !match;
          opt.disabled = !match;
        });
      }

      _removePetChip(petId) {
        const wasTwoPets = this._state.selectedPets.length === 2;
        this._state.selectedPets = this._state.selectedPets.filter(p => p.id !== petId);
        this._selectedPetsContainer?.querySelector(`[data-pet-id="${petId}"]`)?.remove();

        if (!this._state.selectedPets.length) {
          if (this._selectedPetsContainer) this._selectedPetsContainer.style.display = 'none';
          if (this._addPetLink) this._addPetLink.style.display = 'none';
          const sw = this._petSelect?.closest('.fps-pet-select-wrap');
          if (sw) sw.style.display = '';
          if (this._petSelect) {
            [...this._petSelect.options].forEach(opt => { opt.hidden = false; opt.disabled = false; });
          }
          this._resetFromStep(3);
        } else {
          this._updateSummary('pet-name', this._state.selectedPets.map(p => p.name).join(', '));
          if (wasTwoPets) {
            // Went from 2 → 1: restore pet select, reload suites without Double Suite
            const remainingPet = this._state.selectedPets[0];
            const isDog = (remainingPet?.type || '').toLowerCase() === 'dog';
            if (this._addPetLink) this._addPetLink.style.display = isDog ? '' : 'none';
            const sw = this._petSelect?.closest('.fps-pet-select-wrap');
            if (sw) sw.style.display = '';
            this._filterPetSelectByType(remainingPet?.type);
            this._resetFromStep(3);
            this._enableStep(3, { scroll: false });
          }
        }
      }

      // ── Step 2 — Suite size ────────────────────────────────────────────────
      async _loadStep3() {
        this._step3Ctrl?.abort();
        this._step3Ctrl = new AbortController();
        const { signal: sig3 } = this._step3Ctrl;

        const step2 = this._steps[3];
        this._showLoader(step2);

        // Suite click listener is registered once per _loadStep3 call and auto-removed
        // when sig3 is aborted (i.e. when _resetFromStep(3) is called).
        this._suitesGrid?.addEventListener('click', e => {
          const card = e.target.closest('.fps-suite-card');
          if (!card) return;
          this._suitesGrid.querySelectorAll('.fps-suite-card').forEach(c => c.classList.remove('is-selected'));
          card.classList.add('is-selected');
          Object.assign(this._state, {
            suiteVariationId: parseInt(card.dataset.variationId),
            suiteName: card.dataset.suiteName,
            suitePrice: parseFloat(card.dataset.price),
          });
          this._updateSummary('suite', `${card.dataset.suiteName} — $${card.dataset.price}/night`);
          this._resetFromStep(4);
          this._enableStep(4);
        }, { signal: sig3 });

        try {
          const petType = encodeURIComponent(this._state.selectedPets[0]?.type || '');
          const data = await this._get(`/PetSuiteSizes?petType=${petType}`, false, sig3);
          if (sig3.aborted) return;
          this._hideLoader(step2);

          // API returns { suites: [{id, name, description, price}] }
          const suites = data?.suites || (Array.isArray(data) ? data : []);

          if (!suites.length) {
            if (this._suitesGrid) this._suitesGrid.innerHTML = `<p class="fps-error">No suites available. Please call ${supportPhone()}.</p>`;
            return;
          }

          if (this._suitesGrid) {
            this._suitesGrid.innerHTML = '';
            const petCount = this._state.selectedPets.length;
            const suiteMap = this._config.suiteMap || {};
            const filteredSuites = suites.filter(s => {
              const displayName = suiteMap[s.name] || s.name;
              if (petCount >= 2) return displayName === 'Double Suite';
              return displayName !== 'Double Suite';
            });
            filteredSuites.forEach(s => this._suitesGrid.appendChild(this._makeSuiteCard(s)));
          }
        } catch (err) {
          if (sig3.aborted) return;
          console.error('[FPS Boarding] PetSuiteSizes error:', err);
          this._hideLoader(step2);
        }
      }

      _makeSuiteCard(suite) {
        const suiteMap = this._config.suiteMap || {};
        const displayName = suiteMap[suite.name] || suite.name;
        const price = suite.price ?? 0;

        const card = this._tplSuiteCard.content.cloneNode(true).firstElementChild;
        card.dataset.variationId = suite.id;
        card.dataset.suiteName = displayName;
        card.dataset.price = price;

        const imgWrap = card.querySelector('.fps-suite-card__img');
        const placeholder = imgWrap?.querySelector('.fps-card-placeholder');
        const imgUrl = suite.photo_url || suite.imageUrl ||
          this._boardingImgUrl(suite.imageName) || this._boardingImgUrl(displayName);
        if (imgWrap && imgUrl) {
          const img = document.createElement('img');
          img.src = imgUrl; img.alt = displayName;
          img.className = 'fps-boarding-card__img'; img.loading = 'lazy';
          img.onerror = () => { img.remove(); if (placeholder) placeholder.hidden = false; };
          if (placeholder) placeholder.hidden = true;
          imgWrap.classList.remove('bg-[#F7F8FA]');
          imgWrap.appendChild(img);
        }

        card.querySelector('.fps-suite-card__name').textContent = displayName;
        card.querySelector('.fps-suite-card__price').textContent = `$${price}/night`;

        const descEl = card.querySelector('.fps-suite-card__desc');
        if (descEl && suite.description) {
          descEl.textContent = suite.description;
          descEl.style.display = '';
        }

        return card;
      }

      // ── Step 3 — Dates ─────────────────────────────────────────────────────
      async _loadStep4() {
        this._step4Ctrl?.abort();
        this._step4Ctrl = new AbortController();
        const { signal: sig4 } = this._step4Ctrl;

        const step4El = this._steps[4];
        this._showLoader(step4El);

        try {
          const petBreedName = encodeURIComponent(this._state.selectedPets[0]?.breed || '');
          const rawDates = await this._get(
            `/Availability?locationId=${this._state.locationId}&variationId=${this._state.suiteVariationId || ''}&petBreedName=${petBreedName}`,
            false,
            sig4
          );
          if (sig4.aborted) return;
          this._hideLoader(step4El);

          const dates = (rawDates || []).map(d => {
            const raw = typeof d === 'string' ? d : (d.date || d.availableDate || '');
            return raw ? raw.split('T')[0] : null;
          }).filter(Boolean);

          loadFlatpickr(() => {
            if (sig4.aborted) return;
            this._initDatePickers(dates.length ? dates : null);
          });
        } catch (err) {
          if (sig4.aborted) return;
          console.error('[FPS Boarding] Availability error:', err);
          this._hideLoader(step4El);
          loadFlatpickr(() => {
            if (sig4.aborted) return;
            this._initDatePickers(null);
          });
        }
      }

      _localISO(date) {
        const y = date.getFullYear();
        const m = String(date.getMonth() + 1).padStart(2, '0');
        const d = String(date.getDate()).padStart(2, '0');
        return `${y}-${m}-${d}`;
      }

      // FPS-685: dropoffDateTime/pickupDateTime are wall-clock time AT THE FACILITY
      // (this._state.locationTz), not UTC -- sending them bare (no "Z"/offset) risks the API
      // interpreting them as UTC and shifting the appointment by the timezone's offset. Re-derive
      // the zone's UTC offset for this specific date (handles DST) via Intl, then apply it.
      _zonedTimeToUtcISOString(dateTimeStr, timeZone) {
        const [datePart, timePart] = dateTimeStr.split('T');
        const [year, month, day] = datePart.split('-').map(Number);
        const [hour, minute, second] = timePart.split(':').map(Number);

        const asUTC = Date.UTC(year, month - 1, day, hour, minute, second);
        const formatter = new Intl.DateTimeFormat('en-US', {
          timeZone,
          year: 'numeric', month: '2-digit', day: '2-digit',
          hour: '2-digit', minute: '2-digit', second: '2-digit',
          hour12: false,
        });
        const parts = formatter.formatToParts(new Date(asUTC)).reduce((acc, p) => {
          acc[p.type] = p.value;
          return acc;
        }, {});
        // Midnight is sometimes formatted as hour "24" instead of "00".
        const tzHour = parts.hour === '24' ? 0 : Number(parts.hour);
        const tzAsUTC = Date.UTC(Number(parts.year), Number(parts.month) - 1, Number(parts.day), tzHour, Number(parts.minute), Number(parts.second));
        const offset = tzAsUTC - asUTC;

        return new Date(asUTC - offset).toISOString();
      }

      _initDatePickers(availableDates) {
        if (!this._dateRangeCalendar) return;
        const today = this._localISO(new Date());
        const limited = Array.isArray(availableDates) && availableDates.length > 0;

        this._flatpickrRange = flatpickr(this._dateRangeCalendar, {
          mode: 'range',
          inline: true,
          showMonths: 2,
          dateFormat: 'Y-m-d',
          ...(limited ? { enable: availableDates, defaultDate: availableDates[0] } : { minDate: today }),
          onChange: (selectedDates, _str, instance) => {
            // Range not complete yet: drop any previous range so checkout can't book stale dates
            const clearRange = () => {
              Object.assign(this._state, { dropoffDate: null, pickupDate: null, dropoffDateTime: null, pickupDateTime: null });
              if (this._dropoffInput) this._dropoffInput.value = '';
              if (this._pickupInput) this._pickupInput.value = '';
              if (this._dateRangeDisplay) this._dateRangeDisplay.style.display = 'none';
              this._updateSummary('dropoff', '');
              this._updateSummary('pickup', '');
              this._validateBooking();
            };
            if (selectedDates.length < 2) { clearRange(); return; }

            const [start, end] = selectedDates;
            const dropoff = this._localISO(start);
            const pickup = this._localISO(end);

            // Need at least 1 night. Keep the day as drop-off so the next click picks pick-up,
            // instead of leaving a dead 1-day "range" the form never acts on.
            if (dropoff === pickup) { instance.setDate(start); clearRange(); return; }

            // Fill hidden inputs
            if (this._dropoffInput) this._dropoffInput.value = dropoff;
            if (this._pickupInput) this._pickupInput.value = pickup;

            // Update state
            this._state.dropoffDate = dropoff;
            this._state.dropoffDateTime = `${dropoff}T07:00:00`;
            this._state.pickupDate = pickup;
            this._state.pickupDateTime = `${pickup}T12:00:00`;

            // Show selected range display
            if (this._dateRangeDisplay && this._dateRangeDisplayText) {
              this._dateRangeDisplayText.textContent =
                `${this._formatDate(dropoff)}  →  ${this._formatDate(pickup)}`;
              this._dateRangeDisplay.style.display = 'flex';
            }

            // Update summary
            this._updateSummary('dropoff', this._formatDate(dropoff));
            this._updateSummary('pickup', this._formatDate(pickup));

            // Pre-fill optional steps with 'None' as default (user can override by selecting items)
            this._updateSummary('addons', 'None');
            this._updateSummary('grooming', 'None');
            this._updateSummary('groupplay', 'None');

            // Enable optional steps (scroll to 5 only) + vaccination
            this._enableStep(5);
            this._enableStep(6, { scroll: false });
            this._enableStep(7, { scroll: false });
            this._enableStep(8, { scroll: false });
            this._validateBooking();
          },
        });
      }

      _formatDate(iso) {
        try {
          // Parse YYYY-MM-DD directly to avoid timezone shifting
          const [y, m, d] = iso.split('-').map(Number);
          const dt = new Date(y, m - 1, d);
          return dt.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric', year: 'numeric' });
        } catch { return iso; }
      }

      // ── Step 5 — Daily add-ons ─────────────────────────────────────────────
      async _loadStep5() {
        this._step5Ctrl?.abort();
        this._step5Ctrl = new AbortController();
        const { signal: sig5 } = this._step5Ctrl;

        const step5El = this._steps[5];
        this._showLoader(step5El);

        try {
          const petType = encodeURIComponent(this._state.selectedPets[0]?.type || '');
          const data = await this._get(`/AddOns?petType=${petType}`);
          this._hideLoader(step5El);

          // API returns { id, name, variations: [{id, name, price, duration, imageName}] }
          const addons = data?.variations || (Array.isArray(data) ? data : []);

          if (this._addonGrid) {
            this._addonGrid.innerHTML = '';
            if (!addons.length) {
              this._addonGrid.innerHTML = '<p class="fps-info text-[13px] text-[#5B6470]">No daily add-ons available for this booking.</p>';
            } else {
              addons.forEach(a => this._addonGrid.appendChild(this._makeServiceCard(a)));
              this._addonGrid.addEventListener('click', e => {
                const card = e.target.closest('.fps-addon-card');
                if (!card) return;
                const varId = parseInt(card.dataset.variationId);
                const addonName = card.dataset.name;
                const selected = card.classList.toggle('is-selected');
                if (selected) {
                  this._state.addonVariationIds.push(varId);
                  this._state.addonNames.push(addonName);
                } else {
                  this._state.addonVariationIds = this._state.addonVariationIds.filter(id => id !== varId);
                  this._state.addonNames = this._state.addonNames.filter(n => n !== addonName);
                }
                this._updateSummary('addons', this._state.addonNames.join(', ') || 'None');
              }, { signal: sig5 });
            }
          }
        } catch (err) {
          console.error('[FPS Boarding] AddOns error:', err);
          this._hideLoader(step5El);
        }
      }

      // ── Step 6 — Grooming add-on ───────────────────────────────────────────
      async _loadStep6() {
        this._step6Ctrl?.abort();
        this._step6Ctrl = new AbortController();
        const { signal: sig6 } = this._step6Ctrl;

        const step6El = this._steps[6];
        this._showLoader(step6El);

        try {
          const petType = encodeURIComponent(this._state.selectedPets[0]?.type || '');
          const services = await this._get(`/GroomingServices?locationId=${this._state.locationId}&petType=${petType}`);
          this._hideLoader(step6El);

          if (this._groomingGrid) {
            this._groomingGrid.innerHTML = '';
            if (!services?.length) {
              this._groomingGrid.innerHTML = '<p class="fps-info text-[13px] text-[#5B6470]">No grooming services available for this booking.</p>';
            } else {
              services.forEach(s => this._groomingGrid.appendChild(this._makeServiceCard(s, this._tplGroomingCard)));
              this._groomingGrid.addEventListener('click', e => {
                const card = e.target.closest('.fps-addon-card');
                if (!card) return;
                const isSelected = card.classList.contains('is-selected');
                this._groomingGrid.querySelectorAll('.fps-addon-card').forEach(c => c.classList.remove('is-selected'));
                if (!isSelected) {
                  card.classList.add('is-selected');
                  this._state.groomingVariationId = parseInt(card.dataset.variationId);
                  this._state.groomingName = card.dataset.name;
                } else {
                  this._state.groomingVariationId = null;
                  this._state.groomingName = null;
                }
                this._updateSummary('grooming', this._state.groomingName || 'None');
              }, { signal: sig6 });
            }
          }
        } catch (err) {
          console.error('[FPS Boarding] GroomingServices error:', err);
          this._hideLoader(step6El);
        }
      }

      // ── Step 7 — Group play ────────────────────────────────────────────────
      async _loadStep7() {
        this._step7Ctrl?.abort();
        this._step7Ctrl = new AbortController();
        const { signal: sig7 } = this._step7Ctrl;

        const step7El = this._steps[7];
        this._showLoader(step7El);

        try {
          const petType = encodeURIComponent(this._state.selectedPets[0]?.type || '');
          const options = await this._get(`/GroupPlay?locationId=${this._state.locationId}&petType=${petType}`);
          this._hideLoader(step7El);

          // Randem API returns Half Day / Full Day descriptions swapped — correct here
          if (Array.isArray(options) && options.length >= 2) {
            const halfIdx = options.findIndex(o => /half.?day/i.test(o.name || ''));
            const fullIdx = options.findIndex(o => /full.?day/i.test(o.name || ''));
            if (halfIdx >= 0 && fullIdx >= 0 && halfIdx !== fullIdx) {
              [options[halfIdx].description, options[fullIdx].description] =
                [options[fullIdx].description, options[halfIdx].description];
            }
          }

          if (this._groupPlayGrid) {
            this._groupPlayGrid.innerHTML = '';
            if (!options?.length) {
              this._groupPlayGrid.innerHTML = '<p class="fps-info text-[13px] text-[#5B6470]">No group play options available for this booking.</p>';
            } else {
              options.forEach(o => this._groupPlayGrid.appendChild(this._makeServiceCard(o, this._tplGroupPlayCard)));
              this._groupPlayGrid.addEventListener('click', e => {
                const card = e.target.closest('.fps-addon-card');
                if (!card) return;
                const isSelected = card.classList.contains('is-selected');
                this._groupPlayGrid.querySelectorAll('.fps-addon-card').forEach(c => c.classList.remove('is-selected'));
                if (!isSelected) {
                  card.classList.add('is-selected');
                  this._state.groupPlayVariationId = parseInt(card.dataset.variationId);
                  this._state.groupPlayName = card.dataset.name;
                } else {
                  this._state.groupPlayVariationId = null;
                  this._state.groupPlayName = null;
                }
                this._updateSummary('groupplay', this._state.groupPlayName || 'None');
              }, { signal: sig7 });
            }
          }
        } catch (err) {
          console.error('[FPS Boarding] GroupPlay error:', err);
          this._hideLoader(step7El);
        }
      }

      _skipToStep(num) {
        this._enableStep(num);
      }

      // MyTime returns a bare filename (e.g. "petboarding-addon-nailtrim.png"); the file
      // itself is hosted by the store. We serve these from Shopify Files, resolved to the
      // Shopify CDN in randem-config.liquid (window.FPSRandem.boardingImages, keyed by
      // filename). Unlisted filenames return null → the card shows the placeholder.
      _boardingImgUrl(imageName) {
        if (!imageName) return null;
        const name = imageName.trim(); // Randem can return e.g. "Groom\n" with trailing newline
        const map = this._config.boardingImages || {};
        const slug = name.toLowerCase().replace(/\s+/g, '-').replace(/[^a-z0-9-]/g, '');
        return map[name] || map[name.toLowerCase()] || map[slug] || null;
      }

      // Branded fallback for cards with no usable image. The MyTime/Randem boarding API
      // does not return images for suites / grooming / group-play, and add-on `imageName`
      // values do not resolve at a known host — so cards frequently have no image.
      // See FPS-510 notes; replace with real images once a source is confirmed.

      _makeServiceCard(item, tpl = this._tplAddonCardBoarding) {
        const card = tpl.content.cloneNode(true).firstElementChild;
        // Randem may nest price/duration inside variations[0] for grooming/group-play
        const variation = item.variations?.[0];
        const varId = item.id || item.variationId || variation?.id;
        const price = item.price ?? variation?.price ?? item.cost ?? 0;
        const durRaw = parseInt(item.duration ?? variation?.duration) || 0;
        card.dataset.variationId = varId;
        card.dataset.name = item.name;

        // Build image URL: try photo_url, then imageName map, then name-based slug fallback
        const imgUrl = item.photo_url || item.imageUrl ||
          this._boardingImgUrl(item.imageName) ||
          this._boardingImgUrl(item.name);
        const imgWrap = card.querySelector('.fps-addon-card__img');
        const placeholder = imgWrap?.querySelector('.fps-card-placeholder');
        if (imgWrap && imgUrl) {
          const img = document.createElement('img');
          img.src = imgUrl; img.alt = item.name;
          img.className = 'fps-boarding-card__img';
          img.loading = 'lazy';
          img.onerror = () => { img.remove(); if (placeholder) placeholder.hidden = false; };
          if (placeholder) placeholder.hidden = true;
          imgWrap.classList.remove('bg-[#F7F8FA]');
          imgWrap.appendChild(img);
        }

        // Duration: 1440 min = per day, otherwise show in min
        const durLabel = durRaw >= 1440 ? '/ day' : durRaw > 0 ? `${durRaw} min` : '';
        card.querySelector('.fps-addon-card__name').textContent = item.name;

        const priceEl = card.querySelector('.fps-addon-card__price');
        const moreInfoBtn = card.querySelector('.fps-addon-card__more-info');

        if (priceEl) {
          // Add-on cards (step 5) — show price
          priceEl.textContent = `$${price}${durLabel ? ` | ${durLabel}` : ''}`;
        } else if (moreInfoBtn) {
          // Grooming / group-play cards (steps 6, 7) — show More Info popup
          const description = item.description || item.info || item.details || '';
          moreInfoBtn.addEventListener('click', e => {
            e.stopPropagation(); // don't select the card
            this._showServiceInfoPopup(item.name, description);
          });
        }

        return card;
      }

      _showServiceInfoPopup(title, description) {
        const tpl = this.querySelector('#fps-tpl-service-info-popup');
        if (!tpl) return;
        const overlay = tpl.content.cloneNode(true).firstElementChild;
        overlay.querySelector('.fps-service-popup__title').textContent = title;
        overlay.querySelector('.fps-service-popup__description').textContent = description || 'No description available.';
        document.body.appendChild(overlay);
        const close = () => overlay.remove();
        overlay.querySelector('.fps-service-popup__close').addEventListener('click', close);
        overlay.addEventListener('click', e => { if (e.target === overlay) close(); });
      }

      // ── Step 7 — Vaccination ───────────────────────────────────────────────
      _initStep8() {
        this._vaccinationSelect?.addEventListener('change', () => {
          const val = this._vaccinationSelect.value;
          const text = this._vaccinationSelect.options[this._vaccinationSelect.selectedIndex]?.text || val;
          this._state.vaccinationStatus = val;
          this._updateSummary('vaccination', text);
          if (this._vaccinationUploadWrap) {
            this._vaccinationUploadWrap.style.display = val === 'need_to_upload' ? '' : 'none';
          }
          if (val) this._enableStep('summary');
        });

        this._vaccinationUpload?.addEventListener('change', () => {
          this._state.vaccinationFiles = Array.from(this._vaccinationUpload.files);
        });

        this._notesInput?.addEventListener('input', () => {
          this._state.appointmentNotes = this._notesInput.value;
        });

      }

      // ── Summary ────────────────────────────────────────────────────────────
      _updateSummary(field, value) {
        if (field === 'all-reset') {
          [this._summaryPetName, this._summaryServices, this._summaryFacility,
          this._summarySuite, this._summaryAddons, this._summaryGrooming,
          this._summaryGroupPlay, this._summaryVaccination,
          this._summaryDropoff, this._summaryPickup].forEach(el => { if (el) el.textContent = '-'; });
          return;
        }
        const map = {
          'pet-name': this._summaryPetName,
          'facility': this._summaryFacility,
          'suite': this._summarySuite,
          'addons': this._summaryAddons,
          'grooming': this._summaryGrooming,
          'groupplay': this._summaryGroupPlay,
          'services': this._summaryServices,
          'vaccination': this._summaryVaccination,
          'dropoff': this._summaryDropoff,
          'pickup': this._summaryPickup,
        };
        const el = map[field];
        if (el) el.textContent = value || '-';
      }

      _buildServicesString() {
        const parts = [];
        if (this._state.suiteName) parts.push(this._state.suiteName);
        if (this._state.addonNames.length) parts.push(...this._state.addonNames);
        if (this._state.groomingName) parts.push(this._state.groomingName);
        if (this._state.groupPlayName) parts.push(this._state.groupPlayName);
        return parts.join(' | ') || '-';
      }

      _validateBooking() {
        const ready = this._state.selectedPets.length > 0
          && !!this._state.suiteVariationId
          && !!this._state.dropoffDateTime
          && !!this._state.pickupDateTime
          && !!this._state.vaccinationStatus
          && !this._checkingOut; // a date/step change mid-request must not unlock a second submit
        if (this._bookBtn) {
          this._bookBtn.disabled = !ready;
          this._bookBtn.classList.toggle('disabled', !ready);
        }
      }

      // ── Checkout ───────────────────────────────────────────────────────────
      async _handleCheckout() {
        this._validateBooking();
        if (this._bookBtn?.disabled) return;

        this._checkingOut = true;
        this._bookBtn.disabled = true;
        this._bookBtn.textContent = 'Booking…';

        try {
          // Upload vaccination files if needed
          let vaccinationFileContent = null;
          if (this._state.vaccinationStatus === 'need_to_upload' && this._state.vaccinationFiles.length) {
            const ids = await this._uploadVaccinationFiles(this._state.vaccinationFiles);
            vaccinationFileContent = ids.length ? ids.join(',') : null;
          }

          // Vaccination proof: text of the selected option
          const vaccinationProof = this._vaccinationSelect
            ? (this._vaccinationSelect.options[this._vaccinationSelect.selectedIndex]?.text || '')
            : (this._state.vaccinationStatus || '');

          const payload = {
            customerToken: this._state.userToken,
            locationId: String(this._state.locationId),
            // FPS-685: bare "YYYY-MM-DD" dates under fromDate/toDate here were saving as today's
            // date server-side regardless of what was picked. Captured the real MyTime marketplace
            // widget's own reservation-creation request (POST /api/mkp/v1/carts/{id}/cart_items) --
            // it sends full ISO-8601 UTC datetimes under begin_at/end_at, not fromDate/toDate, so
            // this private Randem-proxied /PetBoarding/Checkout endpoint likely expects the same
            // key names underneath. dropoffDateTime/pickupDateTime are wall-clock time AT THE
            // FACILITY, not UTC, so convert using the facility's own timezone before sending.
            begin_at: this._zonedTimeToUtcISOString(this._state.dropoffDateTime, this._state.locationTz),
            end_at: this._zonedTimeToUtcISOString(this._state.pickupDateTime, this._state.locationTz),
            children: this._state.selectedPets.map(pet => ({
              petId: String(pet.id),
              petSuiteId: String(this._state.suiteVariationId),
              petSuiteName: this._state.suiteName || '',
              dailyAddOns: this._state.addonVariationIds.map(id => String(id)),
              groomingServices: [
                ...(this._state.groomingVariationId ? [String(this._state.groomingVariationId)] : []),
                ...(this._state.groupPlayVariationId ? [String(this._state.groupPlayVariationId)] : []),
              ],
              customerNotes: this._state.appointmentNotes || '',
              vaccinationProof,
              vaccinationFileContent,
            })),
          };

          await this._post('/Checkout', payload, true);
          this._showConfirmation();
        } catch (err) {
          console.error('[FPS Boarding] Checkout error:', err);
          if (this._bookBtn) { this._bookBtn.disabled = false; this._bookBtn.textContent = 'BOOK REQUEST'; }
          alert(`Booking failed. Please call ${supportPhone()} for assistance.`);
        } finally {
          this._checkingOut = false;
        }
      }

      async _uploadVaccinationFiles(files) {
        const ids = [];
        for (const file of files) {
          const fd = new FormData();
          fd.append('file', file);
          fd.append('customerId', this._state.customerId);
          try {
            const res = await this._postForm('/ProofOfVaccination', fd);
            if (res.fileId || res.id) ids.push(res.fileId || res.id);
          } catch (err) {
            console.error('[FPS Boarding] Vaccination upload error:', err);
          }
        }
        return ids;
      }

      // Replace the form with an in-place confirmation step (parity with the live
      // /appointment-summary/ page). Previously this rendered a dismissible popup and
      // never reset the book button — closing it left the form stuck on "Booking…".
      _showConfirmation() {
        const tpl = this.querySelector('#fps-tpl-booking-confirmation');
        if (!tpl) {
          // No template to render — at least don't strand the button in a loading state.
          if (this._bookBtn) { this._bookBtn.disabled = false; this._bookBtn.textContent = 'BOOK REQUEST'; }
          return;
        }
        const panel = tpl.content.cloneNode(true).firstElementChild;

        // Populate the request summary from booking state.
        const set = (sel, val) => { const el = panel.querySelector(sel); if (el) el.textContent = (val == null || val === '') ? '—' : val; };
        const yesNo = v => (v ? 'Yes' : 'No');
        const vaccinationText = this._vaccinationSelect
          ? (this._vaccinationSelect.options[this._vaccinationSelect.selectedIndex]?.text || '')
          : (this._state.vaccinationStatus || '');

        set('.fps-confirm__facility', this._state.locationName);
        set('.fps-confirm__pet-name', this._state.selectedPets.map(p => p.name).join(', '));
        set('.fps-confirm__suite', this._state.suiteName
          ? `${this._state.suiteName}${this._state.suitePrice ? ` — $${this._state.suitePrice}/night` : ''}`
          : '');
        set('.fps-confirm__addons', yesNo(this._state.addonVariationIds && this._state.addonVariationIds.length));
        set('.fps-confirm__grooming', yesNo(this._state.groomingVariationId));
        set('.fps-confirm__groupplay', yesNo(this._state.groupPlayVariationId));
        set('.fps-confirm__vaccination', vaccinationText);
        set('.fps-confirm__dropoff', this._formatDate(this._state.dropoffDate));
        set('.fps-confirm__pickup', this._formatDate(this._state.pickupDate));

        // Replace the form: hide every rendered child (steps + summary), keep <template>/<script>.
        Array.from(this.children).forEach(child => {
          if (child.tagName !== 'TEMPLATE' && child.tagName !== 'SCRIPT') child.style.display = 'none';
        });
        this.appendChild(panel);

        // "Book another request" → reload to a fresh form.
        panel.querySelector('.fps-confirm__again')?.addEventListener('click', () => window.location.reload());

        this.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
    }
  );
})();
